/**
 * Studio Plus authentication and authorization composable
 * Handles access control, eligibility checks, and route guards for Studio Plus
 */

import { computed, watch, type ComputedRef } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router, type RouteRecordNormalized } from 'vue-router'
import { useStudioPlusCampaign, useStudioPlusPrice } from '@/components/payment/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

/** Route access check result */
export interface RouteAccessResult {
  allowed: boolean
  redirect?: { name: string }
}

/** Default route location */
export interface DefaultRoute {
  name: string
}

/** Return type of useStudioPlusAuth composable */
export interface UseStudioPlusAuthReturn {
  isAuthLoading: ComputedRef<boolean>
  isAuthFetching: ComputedRef<boolean>
  isFullLoading: ComputedRef<boolean>
  isError: ComputedRef<boolean>
  hasStudioPlus: ComputedRef<boolean>
  isEligible: ComputedRef<boolean>
  isExpiredSubscription: ComputedRef<boolean>
  isCancelledSubscription: ComputedRef<boolean>
  userRole: ComputedRef<string | null>
  studioPlusCampaign: ComputedRef<unknown>
  getDefaultRoute: () => DefaultRoute
  canAccessRoute: (routeMeta: RouteRecordNormalized['meta']) => RouteAccessResult
}

/** Navigation lock to prevent concurrent navigations */
let isNavigating = false

/**
 * Studio Plus authentication and access control
 * Manages subscription status, eligibility, and route guards
 *
 * @returns Auth state and access control functions
 *
 * @example
 * const { hasStudioPlus, isEligible, canAccessRoute } = useStudioPlusAuth()
 * if (hasStudioPlus.value) { ... }
 */
export function useStudioPlusAuth(): UseStudioPlusAuthReturn {
  const store = useStore()
  const route: RouteLocationNormalizedLoaded = useRoute()
  const router: Router = useRouter()


  const userRole = computed(() => store.state.session?.userRole ?? null)
  const currency = computed(() => store.state.currency)
  const isSessionLoading = computed(() => store.state.session.isLoading)
  const { hasAdminOrSupport } = useSessionValues()

  const {
    isLoading: isCampaignLoading,
    isError: isCampaignError,
    isFetching: isCampaignFetching,
    isExpiredSubscription,
    isCancelledSubscription,
    studioPlusCampaign,
  } = useStudioPlusCampaign(selectedAccommodationId)

  const { isLoading: isPriceLoading, studioPlusPrice } = useStudioPlusPrice({
    selectedAccommodationId,
    currency,
    countryCode: null,
    postalCode: null,
    countryState: null,
    taxId: null,
    voucherCode: null,
  })

  const isAuthLoading = computed(() => {
    return isSessionLoading.value || isCampaignLoading.value
  })

  const isAuthFetching = computed(() => {
    return isCampaignFetching.value
  })

  const isFullLoading = computed(() => {
    return isSessionLoading.value || isCampaignLoading.value || isPriceLoading.value
  })

  const isError = computed(() => isCampaignError.value)

  const hasStudioPlus = computed(() => {
    if (!studioPlusCampaign.value) return false
    if (isExpiredSubscription.value) return false
    return true
  })

  const isEligible = computed(() => {
    if (isFullLoading.value) return false
    if (!studioPlusPrice.value) return false
    if (hasStudioPlus.value) return false
    return true
  })

  /**
   * Get default route based on subscription status
   * @returns Default route location
   */
  const getDefaultRoute = (): DefaultRoute => {
    if (hasStudioPlus.value) {
      return { name: 'studio-plus-insights' }
    }
    if (isEligible.value) {
      return { name: 'studio-plus-compare' }
    }
    return { name: 'home' }
  }

  /**
   * Navigate to default route if current route requirements not met
   * @private
   */
  const navigateIfNeeded = (): void => {
    if (isNavigating) return
    if (isAuthLoading.value) return

    const requires = (route.meta?.['requires'] as any) || {}
    if (Object.keys(requires).length === 0) return

    let shouldRedirect = false

    if (requires.hasStudioPlus === true && !hasStudioPlus.value) {
      shouldRedirect = true
    }

    if (requires.hasStudioPlus === false && hasStudioPlus.value) {
      shouldRedirect = true
    }

    if (shouldRedirect) {
      isNavigating = true
      const target = getDefaultRoute()

      if (target.name !== route.name) {
        router.replace(target).finally(() => {
          // Reset after navigation settles
          setTimeout(() => {
            isNavigating = false
          }, 100)
        })
      } else {
        isNavigating = false
      }
    }
  }

  // Watch for changes and navigate if needed
  watch([hasStudioPlus, isAuthLoading, selectedAccommodationId, () => route.name], () => {
    navigateIfNeeded()
  }, { immediate: true })

  /**
   * Check if user can access route based on meta requirements
   * @param routeMeta - Route meta object with requirements
   * @returns Access check result with redirect if denied
   */
  const canAccessRoute = (routeMeta: RouteRecordNormalized['meta']): RouteAccessResult => {
    const requires = (routeMeta?.['requires'] as any) || {}

    if (Object.keys(requires).length === 0) {
      return { allowed: true }
    }

    if (requires.hasStudioPlus !== undefined) {
      if (requires.hasStudioPlus !== hasStudioPlus.value) {
        return { allowed: false, redirect: getDefaultRoute() }
      }
    }

    if (requires.isEligible !== undefined) {
      if (requires.isEligible !== isEligible.value) {
        return { allowed: false, redirect: getDefaultRoute() }
      }
    }

    if (requires.userRole) {
      if (
        (requires.userRole === 'admin' && !hasAdminOrSupport.value) ||
        (requires.userRole !== 'admin' && userRole.value !== requires.userRole)
      ) {
        return { allowed: false, redirect: { name: 'not-admin-error' } }
      }
    }

    return { allowed: true }
  }

  return {
    isAuthLoading,
    isAuthFetching,
    isFullLoading,
    isError,
    hasStudioPlus,
    isEligible,
    isExpiredSubscription,
    isCancelledSubscription,
    userRole,
    studioPlusCampaign,
    getDefaultRoute,
    canAccessRoute,
  }
}